import { Button } from '@design-system';
import { isPrefersReducedMotion } from '@utils/isPrefersReducedMotion';
import { useTranslation } from 'next-i18next';
import { FC, RefObject, useCallback } from 'react';

import { DrawByProductId } from '../../features/draws/types';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { ProductStatus } from '../../types/Analytics';
import { ReleaseType } from '../../types/Products';
import { PurchasingState } from '../../types/Purchasing';
import { ButtonLocation } from '../../types/Subscriptions';
import { NFTProductType } from '../../types/web3/product';
import { useAnalyticsEmitter } from '../../utils/analytics/emitter';
import { SignUpButton } from '../Containers/PurchaseForm/SignUpButton';
import { DrawStickyButtons } from './DrawStickyButtons';

interface Props {
  anchorRef: RefObject<HTMLAnchorElement>;
  buttonLocation?: ButtonLocation;
  draw?: DrawByProductId;
  product: ProductType | NFTProductType;
  productRef: string;
  productState: PurchasingState;
  productStatus: ProductStatus;
  releaseType: ReleaseType;
  unlock: boolean;
}

export const ProductStickyButtons: FC<Props> = ({
  anchorRef,
  buttonLocation = 'product page sticky',
  draw,
  product,
  productRef,
  productState,
  productStatus,
  releaseType,
  unlock,
}) => {
  const { t } = useTranslation('product');
  const isMobile = useMediaQuery('sm');
  const [emitter] = useAnalyticsEmitter({
    'button_location': buttonLocation,
    'product_id': !('tokenId' in product) ? product.id[0]?.id : '',
    'product_status': productStatus,
    'product_title': product.title,
  });

  const handleClick = useCallback(
    (buttonState: string) => {
      if (anchorRef.current) {
        anchorRef.current.scrollIntoView({
          behavior: isPrefersReducedMotion() ? 'instant' : 'smooth',
        });
      }
      emitter('sticky button', 'clicked', {
        'button_state': buttonState,
      });
    },
    [anchorRef, emitter],
  );

  if (releaseType === 'draw' && draw) {
    return (
      <DrawStickyButtons
        draw={draw}
        isMobile={isMobile}
        onClick={(isPreOrder: boolean) => {
          handleClick(isPreOrder ? 'pre-order' : 'draw');
        }}
      />
    );
  }

  switch (productState) {
    case 'COMING_SOON':
      return (
        <SignUpButton
          buttonLocation={buttonLocation}
          fullWidth={false}
          labels={{ normal: t('notifyMe'), success: t('subscribed') }}
          onClick={() => handleClick('notify me')}
          onClickSkipSubscription
          product={product}
          productRef={productRef}
          productStatus={productStatus}
          showIcon
          size={isMobile ? 'sm' : 'md'}
        />
      );
    case 'ENQUIRY':
      return (
        <Button
          label={t('enquire')}
          onClick={() => handleClick('enquire')}
          size={isMobile ? 'sm' : 'md'}
          variant="primary"
        />
      );
    case 'AVAILABLE':
      return (
        <Button
          label={unlock ? t('unlock') : t('goToCheckout')}
          onClick={() => handleClick(unlock ? 'unlock' : 'buy now')}
          size={isMobile ? 'sm' : 'md'}
          variant="primary"
        />
      );
    default:
      return null;
  }
};
